import React, { useRef, useCallback } from 'react';
import { motion, useMotionValue, useSpring } from 'motion/react';
import { SiCplusplus, SiPython, SiLinux, SiArduino } from 'react-icons/si';
import { FiShield, FiTerminal } from 'react-icons/fi';
import DotField from './DotField';
import styles from './Hero.module.css';

const heroContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.3 },
  },
};

const fadeUp = {
  hidden:  { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

const logoHover = {
  scale: 1.22,
  rotate: 8,
  borderColor: 'rgba(255, 255, 255, 0.35)',
  color: '#FAFAFA',
  backgroundColor: 'rgba(24, 24, 27, 0.95)',
  boxShadow: '0 12px 28px rgba(0, 0, 0, 0.6), 0 0 15px rgba(255, 255, 255, 0.05)',
};

const logoTransition = { type: 'spring', stiffness: 300, damping: 15 };

function Hero() {
  const heroRef = useRef(null);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 50, damping: 18 });
  const springY = useSpring(mouseY, { stiffness: 50, damping: 18 });

  const handleMouseMove = useCallback((e) => {
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left - rect.width / 2) / rect.width * 18);
    mouseY.set((e.clientY - rect.top - rect.height / 2) / rect.height * 10);
  }, [mouseX, mouseY]);

  const handleMouseLeave = useCallback(() => {
    mouseX.set(0);
    mouseY.set(0);
  }, [mouseX, mouseY]);

  const logos = [
    { Component: SiPython,    title: 'Python',       cls: styles.logo1, yAnim: [0, -10, 0], dur: 4,   delay: 0   },
    { Component: SiCplusplus, title: 'C++',          cls: styles.logo2, yAnim: [0,  12, 0], dur: 5,   delay: 0.5 },
    { Component: SiLinux,     title: 'Linux',        cls: styles.logo3, yAnim: [0,  -8, 0], dur: 4.5, delay: 1   },
    { Component: SiArduino,   title: 'Arduino',      cls: styles.logo4, yAnim: [0,  10, 0], dur: 3.8, delay: 0.2 },
    { Component: FiShield,    title: 'IoT Security', cls: styles.logo5, yAnim: [0, -12, 0], dur: 5.2, delay: 0.7 },
    { Component: FiTerminal,  title: 'Command Line', cls: styles.logo6, yAnim: [0,   8, 0], dur: 4.2, delay: 0.3 },
  ];

  return (
    <section
      ref={heroRef}
      id="home"
      className={styles.hero}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Interactive dot field — behind everything */}
      <DotField
        className={styles.dotField}
        dotRadius={1.2}
        dotSpacing={18}
        bulgeStrength={72}
        cursorRadius={420}
        gradientFrom="rgba(255,255,255,0.09)"
        gradientTo="rgba(255,255,255,0.03)"
        glowColor="rgba(0,0,0,0)"
        glowRadius={80}
      />

      {/* Content — vertically centred left */}
      <motion.div
        className={styles.content}
        variants={heroContainer}
        initial="hidden"
        animate="visible"
      >
        {/* Subtle mouse parallax on text */}
        <motion.div style={{ x: springX, y: springY }}>
          <motion.div variants={fadeUp} className={styles.statusBadge}>
            <motion.span
              className={styles.dot}
              aria-hidden="true"
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            />
            Open to opportunities
          </motion.div>

          <motion.h1 variants={fadeUp} className={styles.name}>
            Balaram
          </motion.h1>

          <motion.p variants={fadeUp} className={styles.profession}>
            <span className={styles.divider} />
            IoT Security & Cybersecurity
          </motion.p>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        className={styles.scrollCue}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.6 }}
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14M5 12l7 7 7-7" />
          </svg>
        </motion.div>
      </motion.div>

      {/* Floating skill logos */}
      <motion.div
        className={styles.logoContainer}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.0, duration: 0.8 }}
      >
        {logos.map(({ Component, title, cls, yAnim, dur, delay }) => (
          <motion.div
            key={title}
            className={`${styles.floatingLogo} ${cls}`}
            animate={{ y: yAnim }}
            whileHover={logoHover}
            transition={{
              y: { duration: dur, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut', delay },
              default: logoTransition,
            }}
          >
            <Component size={20} title={title} />
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}

export default Hero;
